import React from "react";
import { useNetwork } from "../../hooks/useNetwork";
import useAuth from "../../routes/login/AuthContext";
import {
  ReportSubscription,
  deleteReportSubscription,
  updateReportSubscription,
} from "../../services/AdminApi";
import { Button } from "../../UI/Button";
import { Checkbox } from "../../UI/Checkbox";
import { TableCell } from "../../UI/TableCell";
import { TableHead } from "../../UI/TableHead";
import { TableRow } from "../../UI/TableRow";
import { EnhancedTable } from "../EnhancedTable";
import { EnhancedTableBody } from "../EnhancedTableBody";
import { ReadOnlyWrapper } from "../ReadOnlyWrapper";
import { useSnackbar } from "../../hooks/useSnackbar";
import { EditAdminNotificationButton } from "../EditAdminNotificationButton";

interface IProps {
  isLoading: boolean;
  subscriptions: ReportSubscription[];
  reloadSubscriptions: () => Promise<void>;
}

export const AdminNotificationsTable: React.FC<IProps> = (props) => {
  const { selectedNetwork } = useNetwork();
  const { getAuthToken, isSuperAdminRead } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [busyId, setBusyId] = React.useState<number | null>(null);

  const handleDelete = (id: number) => async () => {
    setBusyId(id);
    const res = await deleteReportSubscription(selectedNetwork.l1Id, id, await getAuthToken());
    if (res.ok) {
      showSuccessSnackbar("Saved Changes");
      await props.reloadSubscriptions();
    } else {
      showErrorSnackbar("An Error Occurred");
    }
    setBusyId(null);
  };

  const handleToggle = (
    subscription: ReportSubscription,
    field: "dailyOptIn" | "weeklyOptIn",
  ) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (subscription.id === undefined) return;
    setBusyId(subscription.id);
    const success = await updateReportSubscription(
      selectedNetwork.l1Id,
      { ...subscription, [field]: e.target.checked },
      await getAuthToken(),
    );
    if (success) {
      showSuccessSnackbar("Saved Changes");
      await props.reloadSubscriptions();
    } else {
      showErrorSnackbar("An Error Occurred");
    }
    setBusyId(null);
  };

  return (
    <EnhancedTable>
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Email</TableCell>
          <TableCell>Daily Reports</TableCell>
          <TableCell>Weekly Reports</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <EnhancedTableBody colSpan={5} isLoading={props.isLoading}>
        {props.subscriptions.map((subscription) => (
          <TableRow key={subscription.id}>
            <TableCell>{subscription.managerName}</TableCell>
            <TableCell>{subscription.managerEmail}</TableCell>
            <TableCell>
              <ReadOnlyWrapper>
                <Checkbox
                  isChecked={subscription.dailyOptIn}
                  onChange={handleToggle(subscription, "dailyOptIn")}
                  isDisabled={isSuperAdminRead() || busyId === subscription.id}
                />
              </ReadOnlyWrapper>
            </TableCell>
            <TableCell>
              <ReadOnlyWrapper>
                <Checkbox
                  isChecked={subscription.weeklyOptIn}
                  onChange={handleToggle(subscription, "weeklyOptIn")}
                  isDisabled={isSuperAdminRead() || busyId === subscription.id}
                />
              </ReadOnlyWrapper>
            </TableCell>
            <TableCell align="right">
              <EditAdminNotificationButton
                subscription={subscription}
                reloadSubscriptions={props.reloadSubscriptions}
              />
              <ReadOnlyWrapper>
                <Button
                  appearance="danger"
                  isLoading={busyId === subscription.id}
                  isDisabled={isSuperAdminRead()}
                  onClick={handleDelete(subscription.id as number)}
                >
                  Delete
                </Button>
              </ReadOnlyWrapper>
            </TableCell>
          </TableRow>
        ))}
      </EnhancedTableBody>
    </EnhancedTable>
  );
};
